import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

export default function AdminCoinRules() {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/coin-rules");
      setRules({ ...data, slabs: data.slabs || [] });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const update = (k) => (e) => {
    const v = e.target.type === "number" ? parseFloat(e.target.value) : e.target.value;
    setRules((r) => ({ ...r, [k]: v }));
  };

  const updateSlab = (i, k, v) => setRules((r) => {
    const slabs = [...(r.slabs || [])];
    if (v === "" || v === null) {
      slabs[i] = { ...slabs[i], [k]: null };
    } else {
      slabs[i] = { ...slabs[i], [k]: parseFloat(v) };
    }
    return { ...r, slabs };
  });

  const addSlab = () => setRules((r) => ({ ...r, slabs: [...(r.slabs || []), { min_inr: 0, max_inr: 500, multiplier: 1, fixed_coins: null }] }));
  const removeSlab = (i) => setRules((r) => ({ ...r, slabs: r.slabs.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/coin-rules", {
        coins_per_inr: parseFloat(rules.coins_per_inr) || 0.01,
        signup_bonus: parseInt(rules.signup_bonus) || 0,
        birthday_bonus: parseInt(rules.birthday_bonus) || 0,
        referral_bonus: parseInt(rules.referral_bonus) || 0,
        slabs: rules.slabs || [],
      });
      toast.success("Coin rules updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!rules) return <div>Loading…</div>;

  const previewAmounts = [100, 500, 1000, 2500];

  return (
    <div data-testid="admin-coin-rules-page">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Loyalty</div>
          <h1 className="editorial-h1 text-4xl">Coin Earning Rules</h1>
        </div>
        <button onClick={save} disabled={saving} className="btn-pill btn-dark text-xs disabled:opacity-50" data-testid="save-rules-btn">
          <Save size={14} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </header>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-white border border-brand-line p-6 space-y-4">
          <h3 className="font-display text-xl">Base earning</h3>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Coins per ₹1 spent</label>
            <input type="number" step="0.001" value={rules.coins_per_inr} onChange={update("coins_per_inr")} className="pp-input" data-testid="coins-per-inr-input" />
            <div className="text-[11px] text-brand-charcoal/50 mt-1">e.g. 0.01 = 1 coin per ₹100 (Starbucks-style)</div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Signup bonus</label><input type="number" value={rules.signup_bonus} onChange={update("signup_bonus")} className="pp-input" data-testid="signup-bonus-input" /></div>
            <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Birthday bonus</label><input type="number" value={rules.birthday_bonus} onChange={update("birthday_bonus")} className="pp-input" data-testid="birthday-bonus-input" /></div>
            <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Referral bonus</label><input type="number" value={rules.referral_bonus} onChange={update("referral_bonus")} className="pp-input" data-testid="referral-bonus-input" /></div>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-brand-line p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-xl">Multiplier slabs</h3>
            <button onClick={addSlab} className="btn-pill btn-ghost text-xs" data-testid="add-slab-btn"><Plus size={12} /> Add slab</button>
          </div>
          <p className="text-xs text-brand-charcoal/55">Optional: define fixed coins per bill bracket (e.g. ₹100-199 → 10 coins). Leave <b>Fixed coins</b> blank to use multiplier instead. First matching slab wins.</p>
          {(!rules.slabs || rules.slabs.length === 0) && <div className="text-sm text-brand-charcoal/45">No slabs. Base rate applies for all amounts.</div>}
          <ul className="space-y-2">
            {(rules.slabs || []).map((s, i) => (
              <li key={i} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`slab-row-${i}`}>
                <input type="number" placeholder="Min ₹" value={s.min_inr ?? 0} onChange={(e) => updateSlab(i, "min_inr", e.target.value)} className="pp-input" />
                <input type="number" placeholder="Max ₹" value={s.max_inr ?? 0} onChange={(e) => updateSlab(i, "max_inr", e.target.value)} className="pp-input" />
                <input type="number" placeholder="Fixed coins" value={s.fixed_coins ?? ""} onChange={(e) => updateSlab(i, "fixed_coins", e.target.value)} className="pp-input" title="Fixed coins for this bracket (overrides multiplier)" />
                <input type="number" step="0.1" placeholder="Multiplier" value={s.multiplier ?? 1} onChange={(e) => updateSlab(i, "multiplier", e.target.value)} className="pp-input" title="Used only if Fixed coins is blank" />
                <button onClick={() => removeSlab(i)} className="text-red-600" data-testid={`remove-slab-${i}`}><Trash2 size={16} /></button>
              </li>
            ))}
            {(rules.slabs || []).length > 0 && (
              <li className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 text-[10px] uppercase tracking-widest text-brand-charcoal/50">
                <span>Min ₹</span><span>Max ₹ (exclusive)</span><span>Fixed coins</span><span>× multiplier</span><span></span>
              </li>
            )}
          </ul>
        </div>

        <div className="rounded-2xl bg-brand-charcoal text-brand-cream p-6 lg:col-span-2">
          <h3 className="font-display text-xl mb-4">Preview earnings</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {previewAmounts.map((a) => {
              const slabs = rules.slabs || [];
              let coins = Math.round(a * (rules.coins_per_inr || 0));
              let mult = 1;
              let fixed = null;
              for (const s of slabs) {
                if (a >= (s.min_inr || 0) && a < (s.max_inr || 1e12)) {
                  if (s.fixed_coins !== undefined && s.fixed_coins !== null && s.fixed_coins !== "") {
                    fixed = Number(s.fixed_coins);
                    coins = fixed;
                  } else {
                    mult = s.multiplier || 1;
                    coins = Math.round(a * (rules.coins_per_inr || 0) * mult);
                  }
                  break;
                }
              }
              return (
                <div key={a} className="rounded-xl bg-white/5 border border-white/10 p-4">
                  <div className="text-[11px] uppercase tracking-widest text-brand-yellow">₹{a} spent</div>
                  <div className="font-display text-3xl mt-1">{coins} <span className="text-sm text-brand-cream/60">coins</span></div>
                  <div className="text-[11px] text-brand-cream/50 mt-1">{fixed !== null ? "fixed" : `multiplier: ${mult}×`}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
